import React, { useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { allGeoRecords } from "../data/kumbhGeo";
import { INCIDENTS } from "../data/operational";
import { LAYER_BY_ID } from "../config/layers";

/* Unified geospatial search (§8).
 *
 * One index over every geo record plus incidents, so an operator types
 * "Ramkund" or an incident ID and gets the same box either way. The index is
 * built from allGeoRecords(), which derives from the layer catalogue — a new
 * layer becomes searchable without touching this file.
 *
 * Matching is intentionally simple (substring over name / id / kind). At this
 * data volume that is instant; at department scale this call moves behind an
 * API with a geospatial index (§13), and only this function changes. */
export default function GeoSearch({ lang = "en", onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  const index = useMemo(() => {
    const geo = allGeoRecords().map((r) => ({
      id: r.id,
      label: r.name?.[lang] ?? r.name?.en ?? r.id,
      sub: LAYER_BY_ID[r.layer]?.label?.[lang] ?? r.kind,
      coord: r.coord,
      trust: r.provenance?.trust,
      zoom: r.kind === "Sector" ? 13.4 : 15.5,
    }));
    const inc = INCIDENTS.map((i) => ({
      id: i.id,
      label: i.id,
      sub: `${i.location?.[lang] ?? i.location?.en} · ${i.status}`,
      coord: i.coord,
      trust: i.provenance?.trust,
      zoom: 16,
      isIncident: true,
    }));
    return [...geo, ...inc];
  }, [lang]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    return index
      .filter(
        (r) =>
          r.label.toLowerCase().includes(needle) ||
          r.id.toLowerCase().includes(needle) ||
          (r.sub || "").toLowerCase().includes(needle)
      )
      .slice(0, 12);
  }, [q, index]);

  const choose = (r) => {
    onSelect?.(r);
    setQ(r.label);
    setOpen(false);
  };

  return (
    <div className="relative w-full max-w-md">
      <div className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-saffron/40">
        <Search size={14} className="text-slate-400 flex-shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          /* Delay the close so a click on a result lands before blur hides it. */
          onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
          placeholder={
            lang === "mr"
              ? "सेक्टर, घाट, रुग्णालय, घटना क्रमांक शोधा…"
              : "Search sector, ghat, hospital, incident ID…"
          }
          className="flex-1 bg-transparent text-sm outline-none placeholder-slate-400"
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); setOpen(false); }} aria-label="Clear">
            <X size={13} className="text-slate-400 hover:text-slate-600" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl max-h-80 overflow-y-auto"
          onMouseEnter={() => clearTimeout(blurTimer.current)}
        >
          {results.length === 0 ? (
            /* Empty state (§15) — says what was searched, not just "no results". */
            <div className="px-3 py-3 text-xs text-slate-500">
              {lang === "mr" ? "काहीही सापडले नाही" : "No match"} — “{q}”
            </div>
          ) : (
            results.map((r) => (
              <button
                key={`${r.id}-${r.label}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(r)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-slate-800 truncate">{r.label}</span>
                  {r.trust === "SIMULATED" && (
                    <span className="text-[8px] font-black px-1 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 flex-shrink-0">
                      SIM
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{r.sub}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
